import * as Sentry from '@sentry/react'
import { useEffect } from 'react'
import { BaseProvider, createTheme, lightThemePrimitives } from 'baseui'
import { Provider as StyletronProvider } from 'styletron-react'
import { Client as Styletron } from 'styletron-engine-atomic'
import { PersistGate } from 'redux-persist/integration/react'
import { createRoot } from 'react-dom/client'
import TagManager from 'react-gtm-module'
import { Provider } from 'react-redux'
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router'
import selectraPrimitives from '@/shared/theme/themePrimitives'
import selectraOverrides from '@/shared/theme/themeOverrides'
import gtmConfig from '@/shared/tools/gtmConfig'
import createStore from '@/store/Store'
import CustomRoutes from './routes/routes'
import '@/shared/theme/reset.css'
import './i18n'
import Echo from 'laravel-echo'
import Pusher from 'pusher-js'
import { runtimeConfig } from '@/runtime-config'
import { NETWORK_ERROR_PATTERNS, sentryBeforeSend } from '@/shared/tools/sentryBeforeSend'
import { buildSentryOptions } from '@/shared/tools/sentryOptions'

if (import.meta.env.PROD) {
  Sentry.init({
    dsn: 'https://209d98c2cb444b9f8be8ee71e36122b0@o1158305.ingest.sentry.io/4505289967271936',
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
        // Turning off only one of the two makes the SDK fall back to the other.
        enableLongAnimationFrame: false,
        enableLongTask: false,
      }),
      // Customer names, IBANs and phone numbers are on screen: keep every replay masked.
      Sentry.replayIntegration({ maskAllText: true, maskAllInputs: true, blockAllMedia: true }),
    ],
    ...buildSentryOptions(runtimeConfig.env, import.meta.env.VITE_SENTRY_RELEASE),
    ignoreErrors: NETWORK_ERROR_PATTERNS,
    beforeSend: sentryBeforeSend,
  })
}

const container = document.getElementById('root')

// BaseUI 16: createTheme ne prend qu'un objet d'overrides
// On merge nos primitives et overrides dans un seul objet
const selectraTheme = createTheme({
  ...lightThemePrimitives,
  ...selectraPrimitives,
  ...selectraOverrides,
})

// reset.css gives this font to the page, so text and native controls without a font of their own match BaseUI
document.documentElement.style.setProperty('--hop-font-family', selectraTheme.typography.font300.fontFamily)

const styletronEngine = new Styletron()
const root = createRoot(container)

const { store, persistor } = createStore()

window.Pusher = Pusher

window.Echo = new Echo({
  broadcaster: 'reverb',
  key: runtimeConfig.LARAVEL_ECHO_KEY,
  wsHost: runtimeConfig.LARAVEL_ECHO_HOST,
  wsPort: runtimeConfig.LARAVEL_ECHO_PORT,
  wssPort: runtimeConfig.LARAVEL_ECHO_PORT,
  forceTLS: runtimeConfig.LARAVEL_ECHO_TLS,
  enabledTransports: ['ws', 'wss'],
})

if (container) {
  const tagManagerArgs = {
    gtmId: gtmConfig('fr'),
  }
  if (import.meta.env.PROD) {
    TagManager.initialize(tagManagerArgs)
  }
}

root.render(
  <Provider store={store}>
    <PersistGate loading='' persistor={persistor}>
      <StyletronProvider value={styletronEngine}>
        <BaseProvider theme={selectraTheme}>
          <CustomRoutes />
        </BaseProvider>
      </StyletronProvider>
    </PersistGate>
  </Provider>,
)
